/**
 * Gandiv — the chart adapter.
 *
 * A higher-timeframe band breakout with a FIXED stop and a chained
 * stop-and-reverse. The RULE is in `gandivEngine.ts` (zero imports, byte-identical
 * backend twin); the band is `utils/gandivBand.ts`. This file turns them into
 * what you validate by eye:
 *
 *   ▲ B / ▼ S   the source close crossed the band (a first entry, or a reversal
 *               because the band was crossed before the stop)
 *   ▲ R / ▼ R   the stop was crossed: closed and reversed, new stop from the chain
 *   ● X         the stop was crossed and no reverse stop was valid: flat
 *   tradeLevels the band as the rule saw it, and each trade's Entry and SL
 *
 * THE BAND DRAWN HERE IS NOT THE CLN CONTEXT BAND. That one shows each HTF
 * window's FINAL body; this one shows the body as it was known at each bar's
 * close, which is what the rule decided on. Inside a window they differ, and on
 * the last bar of a window they meet. Nothing drawn here moves once its bar has
 * closed.
 *
 * PRICES ARE REAL. The source candle (HA / CLH / CLN) only decides; Entry lines
 * sit at the REAL close of the deciding bar, and SLs are band prices.
 *
 * NOT AN ENGINE. No scan, no alert, no order path. It marks a chart.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import {
  ChartStrategy, ChartStrategyOptions, StrategyOverlayResult, ChartMarker, TradeLevelSegment,
} from './types';
import { indicatorSourceCandles, IndicatorSource } from '../indicatorSource';
import { computeGandivBand, gandivClnLength } from '../gandivBand';
import { bandStepUp } from '../bandStepColour';
import { intervalLabel } from '../intervalCatalog';
import { computeGandivTrades, GandivTrade } from './gandivEngine';

/** Marker colours — legible on white and on #0D1117 alike (the adapter cannot
 *  see the theme; Vajra's exit marks had to be lifted twice). */
export const GANDIV_LONG_COLOR = '#00c853';
export const GANDIV_SHORT_COLOR = '#ff5252';
export const GANDIV_STOP_COLOR = '#ff9800';

/** Line defaults, used until the settings group resolves; the dialog owns them. */
export const GANDIV_DEFAULTS = {
  source: 'HA' as IndicatorSource,
  bandUpColor: '#26a69a',
  bandDownColor: '#ef5350',
  longColor: '#00c853',
  shortColor: '#ff5252',
  slColor: '#ff9800',
};

const NL = String.fromCharCode(10);

/** Label price: 2 dp from 100 up (as the chart's axis shows BTC or METAX),
 *  4 dp for a dollar-scale token, 6 below that. */
export function gandivPrice(v: number): string {
  if (!isFinite(v)) return '—';
  const a = Math.abs(v);
  return v.toFixed(a >= 100 ? 2 : a >= 1 ? 4 : 6);
}

const SOURCE_LABEL: Record<string, string> = { Regular: 'Reg', HA: 'HA', CLH: 'CLH', CLN: 'CLN' };

export function resolveGandivSource(v: unknown): IndicatorSource {
  return v === 'Regular' || v === 'HA' || v === 'CLH' || v === 'CLN' ? v : GANDIV_DEFAULTS.source;
}

export function computeGandivOverlay(
  candles: CandleRow[],
  opts?: ChartStrategyOptions,
): StrategyOverlayResult {
  const empty: StrategyOverlayResult = { candleColors: [], markers: [] };
  const N = gandivClnLength(Number(opts?.chartClnLength) || 14);
  const source = resolveGandivSource(opts?.gandivSource);
  const ltfMin = Number(opts?.ltfMinutes) || 0;
  const htfMin = Number(opts?.mappedHtfMinutes) || 0;
  const htf = opts?.mappedHtfCandles || [];

  if (!candles || candles.length < 3) return empty;
  if (!(htfMin > ltfMin) || !(htfMin > 0)) {
    return {
      ...empty,
      notice: 'Gandiv needs a higher timeframe above this chart, and there is none.'
        + NL + 'Map one on the Strategies page; nothing is marked here.',
    };
  }
  if (htf.length < 2) {
    return { ...empty, notice: 'Gandiv is waiting for the ' + intervalLabel(htfMin) + ' bars to load.' };
  }

  // The band is built from REAL bars; only the crossing reads the source candle.
  const band = computeGandivBand(candles, htf, htfMin, N);
  const src = indicatorSourceCandles(candles, source, N);
  const n = candles.length;
  const times = candles.map(c => Number(c.time));
  const res = computeGandivTrades(
    times,
    src.map(c => Number(c.close)),
    candles.map(c => Number(c.close)),
    band.map(b => (b ? b.upper : null)),
    band.map(b => (b ? b.lower : null)),
    // The source's own warm-up: a CLN body needs N bars, HA/CLH a few EMA lengths.
    Math.max(N, 30),
  );

  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();
  for (const s of res.steps) positionByTime.set(s.time, s.pos);

  for (let i = 0; i < res.steps.length; i++) {
    const s = res.steps[i];
    if (!s.event) continue;
    if (s.event === 'SL_FLAT') {
      markers.push({ time: s.time, position: 'aboveBar', text: 'X', color: GANDIV_STOP_COLOR, shape: 'circle', size: 2 });
      const wasLong = i > 0 && res.steps[i - 1].pos === 'LONG';
      signalsByTime.set(s.time, {
        signal: wasLong ? 'EXIT_LONG' : 'EXIT_SHORT', strength: null, pos: 'FLAT', reason: 'GANDIV_SL_FLAT',
      });
      continue;
    }
    const long = s.pos === 'LONG';
    const stop = s.event === 'SL_REV';
    markers.push({
      time: s.time,
      position: long ? 'belowBar' : 'aboveBar',
      text: stop ? 'R' : long ? 'B' : 'S',
      color: stop ? GANDIV_STOP_COLOR : long ? GANDIV_LONG_COLOR : GANDIV_SHORT_COLOR,
      shape: long ? 'arrowUp' : 'arrowDown',
      size: 2,
    });
    signalsByTime.set(s.time, {
      signal: long ? 'BUY' : 'SHORT',
      strength: stop ? 'MEDIUM' : 'HIGH',
      pos: long ? 'LONG' : 'SHORT',
      reason: 'GANDIV_' + s.event + (long ? '_UP' : '_DOWN'),
    });
  }

  // ── levels ─────────────────────────────────────────────────────────────
  const tradeLevels: TradeLevelSegment[] = [];
  const up: TradeLevelSegment = { kind: 'bandUpper', points: [] };
  const lo: TradeLevelSegment = { kind: 'bandLower', points: [] };
  // Colour (lines AND fill): both lines stepping up vs the PREVIOUS CHART BAR ->
  // rising, both down -> falling, otherwise the as-known regression direction.
  // Colour only: the rule never reads it.
  let prevBand: { upper: number; lower: number } | null = null;
  for (const b of band) {
    if (!b) { prevBand = null; continue; }
    const colourUp = bandStepUp(prevBand, b, b.up);
    up.points.push({ time: b.time, value: b.upper, up: colourUp });
    lo.points.push({ time: b.time, value: b.lower, up: colourUp });
    prevBand = { upper: b.upper, lower: b.lower };
  }
  if (up.points.length) tradeLevels.push(up, lo);
  const seg = (t: GandivTrade, kind: 'entry' | 'sl', value: number, label: string): TradeLevelSegment => {
    const last = t.exitIdx == null ? n - 1 : t.exitIdx;
    const points: TradeLevelSegment['points'] = [];
    // Entry bar to exit bar INCLUSIVE: the exit bar is the one whose close
    // crossed, so its level has to be on screen to check that by eye.
    for (let i = t.entryIdx; i <= last; i++) points.push({ time: times[i], value });
    // Labels go on the side AWAY from the trade's other line: a long's Entry is
    // above its SL, so E sits over its line and SL under its; a short mirrors.
    // Both lines start on the same bar, often cents apart on a thin band, and
    // two labels on the same side printed on top of each other.
    const below = (kind === 'sl') === (t.side === 'LONG');
    return { kind, side: t.side, points, label, labelBelow: below, open: t.exitIdx == null };
  };
  for (const t of res.trades) {
    tradeLevels.push(seg(t, 'entry', t.entryPrice, 'E ' + gandivPrice(t.entryPrice)));
    tradeLevels.push(seg(t, 'sl', t.sl, 'SL ' + gandivPrice(t.sl)));
  }

  // ── notice: one short row per line (the badge does not wrap) ───────────
  const rows: string[] = [];
  rows.push(SOURCE_LABEL[source] + ' close vs the ' + intervalLabel(htfMin)
    + ' CLN(' + N + ') band as known at each close.');
  const open = res.trades.length ? res.trades[res.trades.length - 1] : null;
  if (open && open.exitIdx == null) {
    rows.push('Now ' + open.side + ' from ' + gandivPrice(open.entryPrice) + ', SL ' + gandivPrice(open.sl) + '.');
  } else if (!res.trades.length) {
    rows.push(up.points.length ? 'No band cross on this range yet.'
      : 'The band needs ' + N + ' ' + intervalLabel(htfMin) + ' bars; not enough loaded.');
  } else {
    rows.push('Now FLAT, waiting for a band cross.');
  }
  if (res.fallbacks || res.flats) {
    rows.push(res.fallbacks + ' chained SL(s) were on the wrong side and used the band line; '
      + res.flats + ' went flat.');
  }

  return {
    candleColors: [],
    markers,
    signalsByTime,
    positionByTime,
    tradeLevels,
    notice: rows.join(NL),
  };
}

export const gandivStrategy: ChartStrategy = {
  id: 'gandiv',
  name: 'Gandiv',
  computeOverlays: computeGandivOverlay,
};
